/* eslint-disable react/forbid-prop-types */
/* istanbul ignore file */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';
import { Redirect } from 'react-router-dom';
import { Glyphicon } from '@bingads-webui-react/primitive';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { CampaignOData } from '@bingads-webui-react/campaign-odata';
import { SearchResultLiveRegion } from '@bingads-webui-react/search-box';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { ListSelector } from '@bingads-webui-react/list-selector';
import { submitEvent } from '@bingads-webui-universal/accessibility-helper';
import { QueryParam, withScope } from '@bingads-webui-react/scope-service';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withPreference } from '@bingads-webui-react/preference-context';
import { withGlobalFilter } from '@bingads-webui-campaign-react/global-filter-context';
import { featureIdToFilterAlias, featureNameToEntityNameFilter } from '@bingads-webui-campaign/entity-name-filter-metadata';
import { getDefaultCampaignSearchStyles } from './style/default-campaign-search-styles';
import { getRecentCampaigns } from './util';
import { getCampaignOdataConfig } from './constant';

const errorFallback = () => (<Redirect to="campaign/vnext" />);

class CampaignResultView extends React.PureComponent {
  constructor(props) {
    super(props);
    this.searchActivity = props.searchActivity;
    this.recentCampaigns = getRecentCampaigns(props.preferenceService);
    this.loadingFallback = (
      <div className={props.classes.loading}>
        <Spinner />
      </div>);
    this.featureName = 'CampaignsGrid';
  }

  onSelect = (selectedItem) => {
    const {
      scope,
      clear,
      getSearchItemUniqueId,
    } = this.props;

    const searchItemUniqueId = getSearchItemUniqueId(selectedItem);

    this.searchActivity.trace(this.getSearchActivityTraceLog({
      isSearchResultSelected: true,
      selectedItem: searchItemUniqueId,
    }));
    clear();
    scope.append({ campaignId: selectedItem.Id });
    scope.save('/campaign/vnext/adgroups');
  }

  onClickSeeMore = () => {
    const {
      scope,
      clear,
      searchQuery,
    } = this.props;
    scope.remove('campaignId');
    scope.remove('adGroupId');
    scope.save('/campaign/vnext/campaigns');
    clear();

    const filterExpressions = this.getFilterExpressions('newCampaign', searchQuery);
    this.props.updateEntityNameFilter({
      filterType: featureNameToEntityNameFilter[this.featureName],
      filter: filterExpressions,
    });
  }

  getFilterExpressions = (filterAlias, filterArguments) => {
    const allowedFilterAlias = featureIdToFilterAlias[this.featureName];
    const filter = allowedFilterAlias[filterAlias];
    let filterExpressions = '';
    if (allowedFilterAlias && _.isFunction(filter)) {
      filterExpressions = JSON.stringify(filter(filterArguments));
    }

    return filterExpressions;
  }

  getSearchActivityTraceLog = ({
    isSearchResultSelected,
    selectedItem = '',
  }) => ({
    keyword: this.props.searchQuery,
    isSearchResultSelected,
    selectedItem,
  })

  getCampaignListView = _.throttle(() => {
    const { searchQuery, searchResultsNum, i18n } = this.props;
    const {
      renderItem,
      renderHeader,
      onSelect,
      renderDivider,
      renderFooter,
      renderItemsWithDividers,
      applyFilter,
    } = this;

    const ariaLabel = i18n.getString(_TL_('Campaign search result list'));

    if (searchQuery === '') {
      const data = renderItemsWithDividers(this.recentCampaigns);

      return (this.recentCampaigns.length > 0 ? (<ListSelector
        renderHeader={renderHeader}
        renderItem={renderItem}
        renderDivider={renderDivider}
        onSelect={onSelect}
        data={data}
        enableArrowKeysSupport
        ariaLabel={ariaLabel}
      />) : null);
    }

    const limit = searchResultsNum * 2;
    return (
      <CampaignOData
        call={({ currentAccount }) => currentAccount.Campaigns.get(getCampaignOdataConfig())}
        escapeHatch={({ currentAccount }) => (currentAccount ? undefined : { value: [] })}
        fallback={this.loadingFallback}
        onError={errorFallback}
      >
        {(campaigns) => {
          const data = renderItemsWithDividers(applyFilter(searchQuery, campaigns.value));
            return (
              <div>
                <ListSelector
                  renderHeader={renderHeader}
                  renderFooter={data.length > limit ? renderFooter : undefined}
                  renderItem={renderItem}
                  renderDivider={renderDivider}
                  onSelect={onSelect}
                  data={data.length > limit ? _.first(data, limit) : data}
                  enableArrowKeysSupport
                  ariaLabel={ariaLabel}
                />
                {searchQuery && data.length === 0 &&
                this.renderNoResults(searchQuery, ariaLabel)}
              </div>);
             }}
      </CampaignOData>
    );
  }, 10);

  applyFilter = (searchQuery, value) => {
    const data = value
      .filter(campaign => campaign.Name.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((campaign1, campaign2) => (
        campaign1.Name.indexOf(searchQuery) - campaign2.Name.indexOf(searchQuery)
      ));
    return data;
  }

  renderItem = (item) => {
    const { classes } = this.props;
    const campaignGlyphIcon = item.Status === 'Active' ? 'ba-RadioBullet2' : 'ba-PauseListView12';
    const iconStatusClassName = item.Status === 'Active' ? classes.activeStatusIcon : classes.pausedStatusIcon;

    return (
      <div
        className={classnames(classes.searchResultItem, 'global-search-result-item')}
      >
        <div className={classes.searchItemContainer}>
          <div className={classes.statusIconContainer}>
            <Glyphicon
              className={iconStatusClassName}
              glyph={campaignGlyphIcon}
            />
          </div>
          <div className={classnames(classes.searchItemSpan, 'global-search-result-text')} >
            <span title={item.Name}>{item.Name}</span>
          </div>
        </div>
      </div>
    );
  }

  renderItemsWithDividers = (items) => {
    if (!items) {
      return [];
    }

    return items.map(item => [item, { type: 'divider' }]).reduce((a, b) => a.concat(b), []);
  }

  renderNoResultsListItem = (query) => {
    const { classes, i18n } = this.props;
    return (
      <div
        className={classnames(classes.searchResultItem, 'global-search-result-item')}
      >
        <div className={classes.searchItemContainer}>
          <div className={classes.searchItemSpan}>
            <span>{curlyTemplate(i18n.getString(_TL_('No results found for {{searchText}}')))({
              searchText: query,
            })}
            </span>
          </div>
        </div>
      </div>);
  }

  renderNoResults = (query, ariaLabel) => (
    <ListSelector
      renderHeader={_.noop}
      renderItem={() => this.renderNoResultsListItem(query)}
      renderDivider={_.noop}
      onSelect={_.noop}
      data={[{}]}
      ariaLabel={ariaLabel}
    />)

  renderHeader = () => {
    const {
      classes, searchQuery, i18n,
    } = this.props;
    return (
      <div className={classnames(classes.resultHeaderContainer, 'global-search-result-section-header')}>
        <span className={classes.resultHeader} role="heading" aria-level="2">
          {searchQuery === '' ? i18n.getString(_TL_('Recent campaigns: ')) : i18n.getString(_TL_('Campaigns: '))}
        </span>
      </div>);
  }

  renderFooter = () => {
    const {
      classes, i18n,
    } = this.props;
    return (
      <div
        className={classnames(classes.searchResultItem, 'global-search-result-section-footer')}
        onClick={this.onClickSeeMore}
        onKeyDown={submitEvent(() => this.onClickSeeMore())}
        role="menuitem"
        tabIndex={0}
      >
        <div className={classes.searchItemContainer}>
          <span className={classnames(classes.searchItemSpan, classes.searchItemFooterSpan)}>
            <Glyphicon
              className={classes.logo}
              glyph="ba-Forward"
            />{i18n.getString(_TL_('Show all matching campaigns'))}
          </span>
        </div>
      </div>);
  }

  renderDivider = (item, index, key) => {
    const { classes } = this.props;
    return (
      <div
        className={classnames(classes.divider, 'global-search-divider')}
        key={key}
      />);
  }

  render() {
    const {
      classes,
      i18n,
      searchQuery,
    } = this.props;

    const searchResultItemCount = 0;
    return (
      <div className={classes.searchResultDropDown}>
        {this.getCampaignListView()}
        <SearchResultLiveRegion
          totalCount={searchResultItemCount}
          emptyText={curlyTemplate(i18n.getString(_TL_('No results found for {{searchText}}')))({
            searchText: searchQuery,
          })}
          resultText={curlyTemplate(i18n.getString(_TL_('Found {{totalCount}} Results for {{searchText}}')))({
            totalCount: searchResultItemCount,
            searchText: searchQuery,
          })
        }
        />
      </div>);
  }
}

CampaignResultView.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getDefaultCampaignSearchStyles({}, {}),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  userContext: PropTypes.shape({
    CurrentAccount: PropTypes.object,
  }),
  clear: PropTypes.func.isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  searchActivity: PropTypes.any.isRequired,
  getSearchItemUniqueId: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  searchResultsNum: PropTypes.number,
  updateEntityNameFilter: PropTypes.func.isRequired,
};

CampaignResultView.defaultProps = {
  userContext: {},
  searchResultsNum: 4,
};

export const StyledCampaignResultView =
withPreference(withScope(withGlobalFilter(withI18nConsumer(withDefaultStyles(
  CampaignResultView,
  getDefaultCampaignSearchStyles
)))));

export const renderCampaignSearch = ({
  // eslint-disable-next-line react/prop-types
  searchActivity, getSearchItemUniqueId, searchQuery, clear, i18n, searchResultsNum,
}) => (
  <StyledCampaignResultView
    searchActivity={searchActivity}
    getSearchItemUniqueId={getSearchItemUniqueId}
    searchQuery={searchQuery}
    clear={clear}
    i18n={i18n}
    searchResultsNum={searchResultsNum}
    key="campaign-result-view"
  />
);
